import { FullPageScreen } from "@/lib/Components/Screens/FullPageScreen";
import { Link, useMatch, useNavigate } from "@tanstack/react-router";
import Dropzone from "react-dropzone";
import { cn } from "@/lib/utils";
import { UploadIcon } from "lucide-react";
import { useState } from "react";
import { Heading } from "@/components/catalyst/heading";
import { Button } from "@/components/catalyst/button";
import { useDownload } from "@/lib/Utils/useDownload";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { generateRelocationImportTemplate } from "@/app/Import/GraphQL/generateRelocationTemplate";
import { useImportState } from "@/app/Import/Stores/useImportState";
import { readSheet } from "read-excel-file/browser";
import { useGetTransformFileInputToRowData } from "@/app/Import/hooks/useTransformFileInputToRowData";
import { ColumnMappingDrawer } from "@/app/Import/Components/ColumnMappingDrawer";
import { useTranslations } from "use-intl";

export function ImportUploadScreen() {
  const t = useTranslations("import");
  const { params } = useMatch({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/import/$importSupplierId",
  });
  const navigate = useNavigate();

  const { addRelocations } = useImportState();

  const downloadFile = useDownload();
  const { mutateAsync } = useGqlMutation(generateRelocationImportTemplate);
  const transformData = useGetTransformFileInputToRowData(
    params.importSupplierId,
  );

  const [isDisabled] = useState(false);

  return (
    <FullPageScreen>
      <div className="mb-6 flex w-full flex-wrap items-end justify-between gap-4 border-b border-zinc-950/10 pb-6 dark:border-white/10">
        <Heading>{t("upload.heading")}</Heading>
        <div className="flex gap-4">
          <ColumnMappingDrawer supplierId={params.importSupplierId} />
          <Button
            onClick={async () => {
              const data = await mutateAsync({
                supplierId: params.importSupplierId,
              });

              downloadFile(data.generateImportTemplate, `imoova_template.xlsx`);
            }}
          >
            {t("upload.download_template")}
          </Button>
        </div>
      </div>

      <Dropzone
        accept={{
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
            [],
          "application/vnd.ms-excel": [],
          "text/csv": [],
        }}
        onDrop={async (acceptedFiles) => {
          const file = acceptedFiles.at(0);
          if (!file) {
            alert(t("upload.no_file_uploaded"));
            return;
          }

          const rows = await readSheet(file);

          addRelocations(transformData(rows));

          navigate({
            from: "/$tenant/$orgId/supplier/{-$supplierId}",
            to: "/$tenant/$orgId/supplier/{-$supplierId}/import/$importSupplierId/spreadsheet",
            params: (prev) => ({
              ...prev,
              importSupplierId: params.importSupplierId,
            }),
          });
        }}
      >
        {({ getRootProps, getInputProps, isDragActive }) => (
          <div
            {...getRootProps()}
            className={cn(
              "border-muted-foreground/25 hover:bg-muted/25 group relative grid h-52 w-full cursor-pointer place-items-center rounded-lg border-2 border-dashed px-5 py-2.5 text-center transition",
              "ring-offset-background focus-visible:ring-ring focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2",
              isDragActive && "border-muted-foreground/50",
              isDisabled && "pointer-events-none opacity-60",
            )}
          >
            <input {...getInputProps()} />
            {isDragActive ? (
              <div className="flex flex-col items-center justify-center gap-4 sm:px-5">
                <div className="rounded-full border border-dashed p-3">
                  <UploadIcon
                    className="size-7 text-gray-500"
                    aria-hidden="true"
                  />
                </div>
                <p className="font-medium text-gray-500">
                  {t("upload.drag_active")}
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-4 sm:px-5">
                <div className="rounded-full border border-dashed p-3">
                  <UploadIcon
                    className="size-7 text-gray-500"
                    aria-hidden="true"
                  />
                </div>
                <div className="flex flex-col gap-px">
                  <p className="font-medium text-gray-500">
                    {t("upload.drag_inactive_title")}
                  </p>
                  <p className="text-sm text-gray-500/70">
                    {t("upload.drag_inactive_subtitle")}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </Dropzone>

      <p className="mt-2 text-center">
        <Link
          className="text-xs text-blue-400 underline"
          from="/$tenant/$orgId/supplier/{-$supplierId}"
          to="/$tenant/$orgId/supplier/{-$supplierId}/import/$importSupplierId/spreadsheet"
          params={(prev) => ({
            ...prev,
            importSupplierId: params.importSupplierId,
          })}
        >
          {t("upload.continue_without_upload")}
        </Link>
      </p>
    </FullPageScreen>
  );
}
